/**
 * Scoped embed session — used by cpFetch when the Mapper runs inside an iframe.
 * Never stores the host session JWT; only the short-lived embed token + routing context.
 */

export interface MapperEmbedSession {
  token: string;
  tenantId: string;
  appId: string;
  platformEnv: 'prod' | 'sandbox';
}

let activeSession: MapperEmbedSession | null = null;

export function setEmbedSession(session: MapperEmbedSession | null) {
  activeSession = session;
}

export function getEmbedSession(): MapperEmbedSession | null {
  return activeSession;
}

export function clearEmbedSession() {
  activeSession = null;
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = String(token || '').split('.');
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

export function sessionFromEmbedToken(token: string, platformEnv: 'prod' | 'sandbox' = 'sandbox'): MapperEmbedSession | null {
  const payload = decodeJwtPayload(token);
  if (!payload) return null;
  const tenantId = String(payload.tid || payload.tenant || 'dev');
  const appId = String(payload.app_id || '');
  if (!appId) return null;
  const envRaw = String(payload.platform_env || platformEnv || 'sandbox').toLowerCase();
  const resolvedEnv = envRaw === 'prod' ? 'prod' : 'sandbox';
  return { token, tenantId, appId, platformEnv: resolvedEnv };
}
